import { useCallback, useMemo } from 'react';

import { useFormikContext } from 'formik';

import { Deal } from '../gql/dealGql';
import { ProductSlug } from '../gql/generated/graphql';

import { isNumber } from '../libs/utils';
import { ProductAvailability, getGapProductAvailability } from '../types/products';
import { getLtvRatio } from '../utils/deals';

type ProductAvailabilityRules = Partial<
  Record<ProductSlug, (context: AvailabilityContext) => ProductAvailability>
>;

type AvailabilityContext = {
  ltv: number;
  missingInfo: boolean;
  amountFinanced: number | undefined;
  retailBookValue: number | undefined;
};

const availabilityRules: ProductAvailabilityRules = {
  [ProductSlug.Gap]: (context) =>
    getGapProductAvailability({
      amountFinanced: context.amountFinanced,
      retailBookValue: context.retailBookValue,
      missingInfo: context.missingInfo,
    }),
  // VSC is always available if basic info is present
  [ProductSlug.Vsc]: (context) => {
    if (context.missingInfo) {
      return { available: false, reason: 'Missing required information' };
    }
    return { available: true };
  },
};

export const useProductAvailability = () => {
  const { values } = useFormikContext<Deal>();

  // Calculate if we're missing required financial info
  const missingInfo = useMemo(() => {
    return (
      !values.car.year ||
      !values.car.vin ||
      !values.car.mileage ||
      !isNumber(values.car.retail_book_value) ||
      !isNumber(values.car.payoff?.vehicle_payoff) ||
      !isNumber(values.financial_info.sell_rate) ||
      !isNumber(values.financial_info.term) ||
      !values.customer.address?.state
    );
  }, [
    values.car.year,
    values.car.vin,
    values.car.mileage,
    values.car.retail_book_value,
    values.car.payoff?.vehicle_payoff,
    values.financial_info.sell_rate,
    values.financial_info.term,
    values.customer.address?.state,
  ]);

  const ltv = useMemo(() => {
    const af = values.financial_info.amount_financed;
    const rbv = values.car.retail_book_value;
    if (af && rbv) {
      return getLtvRatio(af, rbv);
    }
    return 0;
  }, [values.financial_info.amount_financed, values.car.retail_book_value]);

  const isProductAvailable = useCallback(
    (productSlug: ProductSlug): ProductAvailability => {
      const rule = availabilityRules[productSlug];
      if (!rule) {
        return { available: true };
      }
      return rule({
        ltv,
        missingInfo,
        amountFinanced: values.financial_info.amount_financed,
        retailBookValue: values.car.retail_book_value,
      });
    },
    [ltv, missingInfo, values.financial_info.amount_financed, values.car.retail_book_value],
  );

  const isPackageAvailable = useCallback(
    (productSlugs: ProductSlug[]): ProductAvailability => {
      if (productSlugs.length === 0) {
        return { available: true };
      }

      const unavailable = productSlugs
        .map((slug) => isProductAvailable(slug))
        .find((a) => !a.available);

      return unavailable ?? { available: true };
    },
    [isProductAvailable],
  );

  return {
    isProductAvailable,
    isPackageAvailable,
    ltv,
    missingInfo,
  };
};
